import { buildCasesFromTranscriptions } from './courtCases';
import { isProcessingStatus, normalizeJobStatus } from './transcriptionStatus';
import { TRANSCRIPT_STATUS } from './transcriptCertification';

export const isToday = (dateString) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
};

export const getSourceLabel = (sourceType) => {
  if (sourceType === 'live_recording') return 'Live';
  if (sourceType === 'upload') return 'Upload';
  return sourceType ? String(sourceType) : '—';
};

export const classifyTranscription = (item) => {
  const jobStatus = normalizeJobStatus(item?.status);
  const transcriptStatus = item?.transcript_status;

  return {
    isProcessing: isProcessingStatus(item?.status),
    isComplete: jobStatus === 'complete',
    isFailed: jobStatus === 'failed',
    isDraft: transcriptStatus === TRANSCRIPT_STATUS.DRAFT,
    isCertified: transcriptStatus === TRANSCRIPT_STATUS.CERTIFIED,
    isToday: isToday(item?.created_at),
    sourceLabel: getSourceLabel(item?.source_type),
  };
};

const sortByCreatedDesc = (a, b) =>
  new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();

const sortByCompletedDesc = (a, b) =>
  new Date(b.completed_at || b.created_at || 0).getTime() -
  new Date(a.completed_at || a.created_at || 0).getTime();

export const buildSessionStatusBreakdown = (transcriptions) => {
  const list = Array.isArray(transcriptions) ? transcriptions : [];
  const counts = { Queued: 0, Processing: 0, Complete: 0, Failed: 0 };

  list.forEach((item) => {
    const status = normalizeJobStatus(item?.status);
    if (status === 'queued') counts.Queued += 1;
    else if (status === 'processing') counts.Processing += 1;
    else if (status === 'failed') counts.Failed += 1;
    else counts.Complete += 1;
  });

  return Object.entries(counts)
    .map(([label, value]) => ({ label, value }))
    .filter((entry) => entry.value > 0);
};

export const buildNeedsAttentionQueue = (transcriptions) => {
  const list = Array.isArray(transcriptions) ? transcriptions : [];

  const processing = list
    .filter((item) => isProcessingStatus(item.status))
    .sort(sortByCreatedDesc);

  const readyForReview = list
    .filter(
      (item) =>
        normalizeJobStatus(item.status) === 'complete' &&
        item.transcript_status === TRANSCRIPT_STATUS.DRAFT
    )
    .sort(sortByCompletedDesc);

  const failed = list
    .filter((item) => normalizeJobStatus(item.status) === 'failed')
    .sort(sortByCreatedDesc)
    .slice(0, 5);

  const recentlyCertified = list
    .filter((item) => item.transcript_status === TRANSCRIPT_STATUS.CERTIFIED)
    .sort(sortByCompletedDesc)
    .slice(0, 3);

  return {
    processing,
    readyForReview,
    failed,
    recentlyCertified,
    hasItems: processing.length + readyForReview.length + failed.length > 0,
  };
};

export const buildRegistrarStats = (transcriptions) => {
  const list = Array.isArray(transcriptions) ? transcriptions : [];

  const sessionsToday = list.filter((item) => isToday(item.created_at)).length;
  const processing = list.filter((item) => isProcessingStatus(item.status)).length;
  const awaitingReview = list.filter(
    (item) =>
      normalizeJobStatus(item.status) === 'complete' &&
      item.transcript_status === TRANSCRIPT_STATUS.DRAFT
  ).length;
  const activeCases = buildCasesFromTranscriptions(list).length;
  const totalTranscripts = list.length;
  const hoursRecorded =
    Math.round(
      (list.reduce((total, item) => total + (item.duration || 0), 0) / 3600) * 100
    ) / 100;

  return {
    sessionsToday,
    processing,
    awaitingReview,
    activeCases,
    totalTranscripts,
    hoursRecorded,
  };
};

export const getGreeting = (date = new Date()) => {
  const hour = date.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

export const formatDashboardDate = (date = new Date()) =>
  date.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
